import { useEffect, useState } from "react";
import { ApiError, type InstanceCalendarSourceList } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import { Link } from "react-router-dom";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";

/**
 * Whose calendars this dial shows (KEY-736). Each person puts their OWN connected calendar on the dial — nobody can
 * add someone else's — and decides whether others see what their events are called; by default everyone else sees
 * each one as "Busy" at its time, and an event marked private in the calendar is Busy to them whatever is chosen
 * here. The owner or a dial admin can take a calendar off; the connection itself stays the person's (Your settings).
 */
export function CalendarsSection({ instanceId, isDialAdmin, onChanged }: { instanceId: string; isDialAdmin: boolean; onChanged: () => void }) {
  const [list, setList] = useState<InstanceCalendarSourceList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");
  const [share, setShare] = useState(false);

  const refresh = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const next = await api.instanceCalendarSources(token, instanceId);
      setList(next);
      setPick((current) => (next.myOtherConnections.some((c) => c.id === current) ? current : next.myOtherConnections[0]?.id ?? ""));
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't load this dial's calendars.");
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  async function run(work: (token: string) => Promise<void>) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await work(token);
      await refresh();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!list) {
    return (
      <SettingsCard title="Calendars" subtitle="Whose diaries this dial shows the coming week from.">
        {error ? <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{error}</div> : null}
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      title="Calendars"
      subtitle="Whose diaries this dial shows the coming week from. Everyone else sees your events as Busy unless you choose to share what they're called."
    >
      {list.sources.length === 0 && <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>No calendars on this dial yet.</div>}
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {list.sources.map((s) => (
          <li key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
              <div style={{ fontSize: 13.5, color: "var(--text)" }}>
                {s.isMine ? `Yours${s.accountLabel ? ` · ${s.accountLabel}` : ""}` : `${s.ownerName}'s calendar`}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                {!s.connected ? "No longer connected" : s.shareDetails ? "Titles shared with this dial" : "Others see Busy"}
              </div>
            </div>
            {s.isMine && s.connected && (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text)" }}>
                <input
                  type="checkbox"
                  checked={s.shareDetails}
                  disabled={busy}
                  onChange={(e) => void run((t) => api.setInstanceCalendarShare(t, instanceId, s.id, e.target.checked))}
                />
                Share titles
              </label>
            )}
            {(s.isMine || isDialAdmin) && (
              <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void run((t) => api.removeInstanceCalendarSource(t, instanceId, s.id))}>
                Take off
              </button>
            )}
          </li>
        ))}
      </ul>

      <div data-help-anchor={HELP_ANCHORS.calendarOnDial} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {list.canAdd && list.myOtherConnections.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select aria-label="Your calendar" value={pick} disabled={busy} onChange={(e) => setPick(e.target.value)} style={{ font: "inherit", fontSize: 14, padding: "7px 10px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}>
            {list.myOtherConnections.map((c) => <option key={c.id} value={c.id}>{c.accountLabel}</option>)}
          </select>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text)" }}>
            <input type="checkbox" checked={share} disabled={busy} onChange={(e) => setShare(e.target.checked)} />
            Share titles
          </label>
          <button type="button" className="kd-btn kd-btn-sm" disabled={busy || !pick} onClick={() => void run(async (t) => {
            await api.addInstanceCalendarSource(t, instanceId, pick, share);
            setShare(false);
          })}>
            Show on this dial
          </button>
        </div>
      )}
      {/* KEY-769: say why there is nothing to press, rather than leave the card looking broken. */}
      {!list.canAdd && (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Only this dial's editors and admins can put their calendar on it.</div>
      )}
      {list.canAdd && list.myOtherConnections.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          To add your calendar, first connect it in <Link to="/settings#calendars">Your settings</Link>.
        </div>
      )}
      </div>
      {error && <div role="status" style={{ fontSize: 13, color: "var(--text-muted)" }}>{error}</div>}
    </SettingsCard>
  );
}
